import React, { useEffect, lazy } from 'react'
import { Dispatch } from 'redux'
import { InfoOutlined } from '@material-ui/icons'
import { IMediaType } from '../../types/mediaType'
import { Modals } from '../../types/modal'
import { getMediaTypeById, deleteMediaType } from '../../actions/mediaType'
import { modalClose, modalOpen } from '../../actions/modal'
import { load } from '../../utils/load'
import { Main, Scrollable, SettingFormsWrapper } from '../../layouts'
import { UpdateMediaTypeForm } from '../../forms'
import {
  CombinedTitle,
  TabsHead,
  View,
  SettingsMenu,
  P,
  SettingDeleteArea,
} from '../../components'

// Lazy load modules
const Modal = load(lazy(() => import('../../components/Modal')))

export interface IMediaTypeSettingsPage {
  match: { params: { extension: string } }
  mediaType: IMediaType
  activeModal?: string
  editable: boolean
  dispatch: Dispatch
}

export const MediaTypeSettingsPage = ({
  match: {
    params: { extension },
  },
  mediaType,
  activeModal,
  editable,
  dispatch,
}: IMediaTypeSettingsPage) => {
  useEffect(() => {
    dispatch(getMediaTypeById(extension))
  }, [extension, dispatch])

  return (
    <Main
      sidebarSecondary={<SettingsMenu />}
      titleBlock={
        <>
          <CombinedTitle first={mediaType.title} second="Media Type Settings" />
          <TabsHead
            tabs={[
              {
                label: 'Details',
                value: 'details',
                icon: <InfoOutlined />,
              },
            ]}
          />
        </>
      }
      helmet="Administration: Media Types"
    >
      <View keep tab="details">
        <Scrollable>
          <SettingFormsWrapper>
            <UpdateMediaTypeForm mediaType={mediaType} editable={editable} />
            <SettingDeleteArea
              onClickHandler={() =>
                dispatch(modalOpen(Modals.deleteConfirmModal))
              }
              texts={{
                title: 'Delete media type',
                description:
                  'After media type deletion, users won’t be able to upload files associated with this media type. All files that already have been uploaded will remain on the portal. Also please note that this action is not reversible and cannot be undone.',
                buttonLabel: 'Delete this Media Type',
              }}
              buttonProps={{ id: 'delete-media-type-dialog' }}
              disabled={!editable}
            />
          </SettingFormsWrapper>
        </Scrollable>
        <Modal
          open={activeModal === Modals.deleteConfirmModal}
          handleClose={() => {
            dispatch(modalClose())
          }}
          action={() => {
            dispatch(deleteMediaType(mediaType.extension))
            dispatch(modalClose())
          }}
          title="Media Type deletion"
          actionLabel="Delete"
        >
          <P mb="true">
            This Media Type <strong>{mediaType.extension}</strong> will be
            deleted - please note that this action is not reversible and cannot
            be undone.
          </P>
          <P>
            Are you sure you want to delete the Media Type{' '}
            <strong>{mediaType.extension}</strong>?
          </P>
        </Modal>
        <Modal
          id="delete-pack-modal-dialog"
          type="info"
          open={activeModal === Modals.deleteStatusModal}
          handleClose={() => {
            dispatch(modalClose())
          }}
          title={`This Media Type ${mediaType.extension} can’t be deleted`}
        >
          <P>{mediaType.message}</P>
        </Modal>
      </View>
    </Main>
  )
}
